import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plant, Storefront, Leaf, Basket, Drop, Fish, Bird } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useSeo, breadcrumbs, offerList, localBusiness, eventList } from "@/lib/seo";
import { PublicShell, Crumbs } from "@/components/PublicShell";
import { ShareBar } from "@/components/ShareBar";
import { NotifyMe } from "@/components/NotifyMe";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function FarmersPublic() {
  const [board, setBoard] = useState(null);

  useEffect(() => {
    api.get("/farm/public").then(({ data }) => setBoard(data)).catch(() => setBoard({ produce: [] }));
  }, []);

  // Everything on the board, told to search engines as products with prices, a local trading
  // place, picking days as events, and the trail back home.
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      breadcrumbs([["Home", "/"], ["Farmers market", "/farmers-market"]]),
      localBusiness({
        name: "Unity & Utility local farmers market",
        description: "Local growers, bakers, dairies, boats and butchers selling fresh produce, eggs, milk, fish and meat at their own prices, with pick-your-own and doorstep delivery.",
        path: "/farmers-market",
        image: board?.image_url ? `${BACKEND}${board.image_url}` : undefined,
        type: "GroceryStore",
        area: "Worldwide",
      }),
      offerList("On the stalls today", "/farmers-market", [
        ...(board?.produce || []).map((p) => ({
          name: p.title, description: `${p.kind}${p.organic ? ", organic" : ""} from ${p.stall_name}`,
          image: p.image_url ? `${BACKEND}${p.image_url}` : undefined,
          price: p.price, symbol: p.symbol, unit: `per ${p.unit}`,
        })),
        ...(board?.milk || []).map((m) => ({
          name: `${m.kind}, ${m.size} ${m.container}`, description: `Fresh from ${m.stall_name}`,
          image: m.image_url ? `${BACKEND}${m.image_url}` : undefined,
          price: m.price, symbol: m.symbol, unit: `per ${m.container}`,
        })),
        ...(board?.catch || []).map((c) => ({
          name: c.local_name ? `${c.species} (${c.local_name})` : c.species,
          description: `${c.state}${c.harbour ? ` from ${c.harbour}` : ""}`,
          image: c.image_url ? `${BACKEND}${c.image_url}` : undefined,
          price: c.price, symbol: c.symbol, unit: c.sell_by,
        })),
        ...(board?.meat || []).map((m) => ({
          name: m.breed ? `${m.kind}, ${m.breed}` : m.kind,
          description: `${m.method}${m.feed ? `, ${m.feed}` : ""} from ${m.stall_name}`,
          image: m.image_url ? `${BACKEND}${m.image_url}` : undefined,
          price: m.price, symbol: m.symbol, unit: m.sell_by,
        })),
      ]),
      ...eventList((board?.fields || []).map((f) => ({
        name: `Pick your own ${f.crop} — ${f.title}`,
        description: `${f.price_basis}${f.bring ? `. Bring ${f.bring}` : ""}`,
        image: f.image_url ? `${BACKEND}${f.image_url}` : undefined,
        start: f.open_from, end: f.open_to || undefined,
        place: f.stall_name, where: f.where, price: f.price, symbol: f.symbol,
      }))),
    ],
  };

  useSeo({
    title: "Farmers market near you — fresh eggs, bread, milk, fish and meat at the seller's own price | Unity & Utility",
    description: "Buy local produce straight from the people who grew it: free range eggs, this morning's bread, back-garden vegetables, honey, doorstep milk, fish off the boat and meat with the method stated. Pick your own apples, peppers and onions. Collection or delivery near you.",
    path: "/farmers-market",
    image: board?.image_url ? `${BACKEND}${board.image_url}` : undefined,
    jsonLd,
  });

  return (
    <PublicShell>
      <div className="mx-auto max-w-5xl px-5 py-10 sm:px-8 sm:py-14" data-testid="farmers-public-page">
        <Crumbs items={[["Home", "/"], ["Farmers market", "/farmers-market"]]} />
        <p className="overline text-[#B3241B]">Local farmers market</p>
        <h1 className="font-display mt-2 flex items-start gap-3 text-4xl font-bold leading-[1.05] tracking-tight text-[#0F172A] sm:text-5xl">
          <Plant size={34} weight="duotone" className="mt-1 shrink-0 text-[#0B5C2E]" />
          Straight from the farm
        </h1>
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-700" data-testid="farmers-public-intro">
          Free range eggs, this morning's bread, vegetables out of the back garden, honey from
          the hive, jam, plants and cut flowers. Growers and bakers anywhere in the world open a
          stall and put their own price on; buyers can take that price or write in what they are
          willing to pay. Every lot is photographed the morning it was picked, then collected at
          the gate or dropped round inside the stall's own delivery radius. Book a slot to pick
          your own apples, peppers and onions, or put fresh milk on your doorstep. Boats and
          fishmongers land the day's catch too — prawns, crab, bombil, levta, onjal and surmai,
          named the way the harbour names it.
        </p>
        <div className="mt-5">
          <ShareBar
            url={board?.share_token
              ? `${window.location.origin}/p/${board.share_token}`
              : `${window.location.origin}/farmers-market`}
            text="Eggs, bread, back-garden vegetables and honey — priced by the people who grew it."
            testid="farmers-public-share" instagram marketplace />
        </div>

        {board?.image_url && (
          <img src={`${BACKEND}${board.image_url}`}
            alt="A farmers market trestle of eggs, warm loaves, vegetables and jars of honey"
            loading="lazy" className="mt-8 h-56 w-full border border-border object-cover sm:h-80"
            data-testid="farmers-public-image" />
        )}

        <section className="mt-6 border border-[#0B5C2E] bg-[#F3FAF5] p-5" data-testid="farmers-public-card">
          <p className="overline text-[#0B5C2E]">
            Today{board?.day ? ` · ${board.day}` : ""}
            {board?.auto_posted && <span className="ml-2">Posted to Facebook</span>}
          </p>
          <div className="mt-3 grid grid-cols-3 gap-3 sm:grid-cols-7">
            {[["Lots of produce", board?.produce_count, "farmers-stat-produce"],
              ["Organic", board?.organic, "farmers-stat-organic"],
              ["Pick your own", board?.fields_count, "farmers-stat-fields"],
              ["Fresh milk", board?.milk_count, "farmers-stat-milk"],
              ["Fresh catch", board?.catch_count, "farmers-stat-catch"],
              ["Meat & poultry", board?.meat_count, "farmers-stat-meat"],
              ["Stalls", (board?.stalls || []).length, "farmers-stat-stalls"]].map(([l, v, t]) => (
              <div key={t} className="border border-border bg-white p-3" data-testid={t}>
                <p className="font-display text-2xl font-bold text-[#0F172A]">{v ?? 0}</p>
                <p className="text-xs text-slate-500">{l}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-8" data-testid="farmers-public-produce">
          <h2 className="font-display text-base font-bold tracking-tight text-[#0F172A] md:text-lg">
            On the stalls today
          </h2>
          {board?.section_images?.produce && (
            <img src={`${BACKEND}${board.section_images.produce}`} loading="lazy"
              alt="Free range eggs in cartons, warm loaves and jars of honey on a market stall"
              className="mt-3 h-44 w-full border border-border object-cover sm:h-56"
              data-testid="farmers-public-produce-image" />
          )}
          {(board?.produce || []).length === 0 ? (
            <p className="mt-2 text-sm text-slate-600" data-testid="farmers-public-produce-empty">
              Nothing on the stalls just yet — the first growers and bakers round here are
              signing up. Open a stall and put your eggs, bread or vegetables up with your
              own price on them.
            </p>
          ) : (
            <div className="mt-3 grid gap-3 sm:grid-cols-3">
              {board.produce.map((p, i) => (
                <div key={i} className="border border-border bg-white p-4" data-testid={`farmers-public-lot-${i}`}>
                  {p.image_url && (
                    <img src={`${BACKEND}${p.image_url}`} alt={p.title} loading="lazy"
                      className="mb-2 h-36 w-full border border-border object-cover"
                      data-testid={`farmers-public-lot-photo-${i}`} />
                  )}
                  <p className="text-sm font-semibold text-[#0F172A]">{p.title}</p>
                  <p className="text-xs text-slate-500">
                    {p.kind} · {p.stall_name}{p.where ? ` · ${p.where}` : ""}
                    {p.organic ? " · organic" : ""}
                  </p>
                  <p className="mt-1 text-sm font-semibold text-[#0B5C2E]">
                    {p.symbol}{Number(p.price || 0).toFixed(2)} per {p.unit}
                  </p>
                  <p className="text-xs text-slate-500">
                    <Leaf size={12} className="mr-1 inline align-[-2px] text-[#0B5C2E]" />
                    Picked {p.picked_on} · {p.quantity} left
                  </p>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="mt-8" data-testid="farmers-public-fields">
          <h2 className="font-display text-base font-bold tracking-tight text-[#0F172A] md:text-lg">
            Pick your own this week
          </h2>
          {board?.section_images?.fields && (
            <img src={`${BACKEND}${board.section_images.fields}`} loading="lazy"
              alt="A family picking apples into wooden crates in an orchard"
              className="mt-3 h-44 w-full border border-border object-cover sm:h-56"
              data-testid="farmers-public-fields-image" />
          )}
          {(board?.fields || []).length === 0 ? (
            <p className="mt-2 text-sm text-slate-600" data-testid="farmers-public-fields-empty">
              No rows open for picking yet. Growers: open a field for apples, peppers, onions,
              strawberries or pumpkins, set your slots and let families book in.
            </p>
          ) : (
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {board.fields.map((f, i) => (
                <div key={i} className="border border-border bg-white p-4" data-testid={`farmers-public-field-${i}`}>
                  {f.image_url && (
                    <img src={`${BACKEND}${f.image_url}`} alt={f.title} loading="lazy"
                      className="mb-2 h-36 w-full border border-border object-cover" />
                  )}
                  <p className="text-sm font-semibold text-[#0F172A]">
                    <Basket size={14} className="mr-1 inline align-[-2px] text-[#B3241B]" />{f.title}
                  </p>
                  <p className="text-xs text-slate-500">
                    {f.crop} · {f.stall_name}{f.where ? ` · ${f.where}` : ""} ·
                    {" "}{(f.days || []).join(", ")} · {(f.slot_times || []).join(" / ")}
                  </p>
                  <p className="mt-1 text-sm font-semibold text-[#0B5C2E]">
                    {f.symbol}{Number(f.price || 0).toFixed(2)} {f.price_basis}
                  </p>
                  <p className="text-xs text-slate-500">
                    {[f.children_ok && "children welcome", f.dogs_ok && "dogs on a lead",
                      f.wheelchair_access && "wheelchair access", f.parking && "parking",
                      f.picnic_area && "picnic area", f.tractor_ride && "tractor ride",
                      f.honesty_box && "honesty box"].filter(Boolean).join(" · ") || "just bring a bag"}
                    {f.bring ? ` · bring ${f.bring}` : ""}
                  </p>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="mt-8" data-testid="farmers-public-milk">
          <h2 className="font-display text-base font-bold tracking-tight text-[#0F172A] md:text-lg">
            Fresh milk on the doorstep
          </h2>
          {board?.section_images?.milk && (
            <img src={`${BACKEND}${board.section_images.milk}`} loading="lazy"
              alt="Glass bottles of fresh milk with foil tops on a dairy step at first light"
              className="mt-3 h-44 w-full border border-border object-cover sm:h-56"
              data-testid="farmers-public-milk-image" />
          )}
          {(board?.milk || []).length === 0 ? (
            <p className="mt-2 text-sm text-slate-600" data-testid="farmers-public-milk-empty">
              No dairy on the board yet. Dairies: put your milk up in glass or carton, say how
              far the round goes, and customers pick their days.
            </p>
          ) : (
            <div className="mt-3 grid gap-3 sm:grid-cols-3">
              {board.milk.map((m, i) => (
                <div key={i} className="border border-border bg-white p-4" data-testid={`farmers-public-milk-${i}`}>
                  {m.image_url && (
                    <img src={`${BACKEND}${m.image_url}`} alt={m.kind} loading="lazy"
                      className="mb-2 h-32 w-full border border-border object-cover" />
                  )}
                  <p className="text-sm font-semibold text-[#0F172A]">
                    <Drop size={14} className="mr-1 inline align-[-2px] text-[#002FA7]" />{m.kind}
                  </p>
                  <p className="text-xs text-slate-500">
                    {m.size} {m.container} · {m.stall_name}{m.where ? ` · ${m.where}` : ""}
                  </p>
                  <p className="mt-1 text-sm font-semibold text-[#0B5C2E]">
                    {m.symbol}{Number(m.price || 0).toFixed(2)} a {m.container}
                  </p>
                  {m.note && <p className="text-xs text-slate-500">{m.note}</p>}
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="mt-8" data-testid="farmers-public-catch">
          <h2 className="font-display text-base font-bold tracking-tight text-[#0F172A] md:text-lg">
            Landed today — fish, prawns and crab
          </h2>
          {board?.section_images?.catch && (
            <img src={`${BACKEND}${board.section_images.catch}`} loading="lazy"
              alt="Baskets of prawns, crab and Bombay duck on crushed ice at a quayside stall"
              className="mt-3 h-44 w-full border border-border object-cover sm:h-56"
              data-testid="farmers-public-catch-image" />
          )}
          {(board?.catch || []).length === 0 ? (
            <p className="mt-2 text-sm text-slate-600" data-testid="farmers-public-catch-empty">
              Nothing landed on the board yet. Boats and fishmongers: put the day's catch up —
              prawns, crab, bombil, levta, onjal, surmai — named the way your harbour names it.
            </p>
          ) : (
            <div className="mt-3 grid gap-3 sm:grid-cols-3">
              {board.catch.map((c, i) => (
                <div key={i} className="border border-border bg-white p-4" data-testid={`farmers-public-catch-${i}`}>
                  {c.image_url && (
                    <img src={`${BACKEND}${c.image_url}`} alt={c.species} loading="lazy"
                      className="mb-2 h-32 w-full border border-border object-cover" />
                  )}
                  <p className="text-sm font-semibold text-[#0F172A]">
                    <Fish size={14} className="mr-1 inline align-[-2px] text-[#002FA7]" />
                    {c.species}{c.local_name ? ` · ${c.local_name}` : ""}
                  </p>
                  <p className="text-xs text-slate-500">
                    {c.state}{c.boat ? ` · off the ${c.boat}` : ""}
                    {c.harbour ? ` · ${c.harbour}` : ""} · {c.stall_name}
                  </p>
                  <p className="mt-1 text-sm font-semibold text-[#0B5C2E]">
                    {c.symbol}{Number(c.price || 0).toFixed(2)} {c.sell_by}
                  </p>
                  <p className="text-xs text-slate-500">
                    {(c.preps || []).join(" · ")}{c.ice_pack ? " · ice pack on delivery" : ""}
                  </p>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="mt-8" data-testid="farmers-public-meat">
          <h2 className="font-display text-base font-bold tracking-tight text-[#0F172A] md:text-lg">
            Fresh meat and poultry — with the method stated
          </h2>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {board?.section_images?.meat && (
              <img src={`${BACKEND}${board.section_images.meat}`} loading="lazy"
                alt="A butcher's counter with fresh goat and mutton cuts and whole dressed chickens"
                className="h-44 w-full border border-border object-cover sm:h-56"
                data-testid="farmers-public-meat-image" />
            )}
            {board?.section_images?.poultry && (
              <img src={`${BACKEND}${board.section_images.poultry}`} loading="lazy"
                alt="A red rooster among free range brown hens in a farmyard run"
                className="h-44 w-full border border-border object-cover sm:h-56"
                data-testid="farmers-public-poultry-image" />
            )}
          </div>
          <p className="mt-2 text-sm leading-relaxed text-slate-600" data-testid="farmers-public-meat-note">
            Every lot says how it was slaughtered and shows the licence the stall trades under.
            Jhatka severs the head with a single instant strike and is favoured in many Sikh and
            Hindu traditions; halal (zabiha) and kosher (shechita) follow Islamic and Jewish law.
            Live birds and animals are collection only, and anything can be prepared to order on
            the day.
          </p>
          {(board?.meat || []).length === 0 ? (
            <p className="mt-2 text-sm text-slate-600" data-testid="farmers-public-meat-empty">
              Nothing on the meat board yet. Butchers, poultry farms and goat farms: put your
              chicken, rooster, kid or mutton up with the method and your licence on it.
            </p>
          ) : (
            <div className="mt-3 grid gap-3 sm:grid-cols-3">
              {board.meat.map((m, i) => (
                <div key={i} className="border border-border bg-white p-4" data-testid={`farmers-public-meat-${i}`}>
                  {m.image_url && (
                    <img src={`${BACKEND}${m.image_url}`} alt={m.kind} loading="lazy"
                      className="mb-2 h-32 w-full border border-border object-cover" />
                  )}
                  <p className="text-sm font-semibold text-[#0F172A]">
                    <Bird size={14} className="mr-1 inline align-[-2px] text-[#B3241B]" />
                    {m.kind}{m.breed ? ` · ${m.breed}` : ""}
                  </p>
                  <p className="text-xs font-semibold text-[#B3241B]">{m.method}</p>
                  <p className="text-xs text-slate-500">
                    {m.state}{m.weight_range ? ` · ${m.weight_range}` : ""}
                    {m.age ? ` · ${m.age}` : ""} · {m.feed} · {m.stall_name}
                  </p>
                  <p className="mt-1 text-sm font-semibold text-[#0B5C2E]">
                    {m.symbol}{Number(m.price || 0).toFixed(2)} {m.sell_by}
                  </p>
                  <p className="text-xs text-slate-500">{(m.cuts || []).join(" · ")}</p>
                  <p className="text-[11px] text-slate-500">
                    {m.licence_authority || "Licence"} {m.licence_number}
                  </p>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="mt-8" data-testid="farmers-public-stalls">
          <h2 className="font-display text-base font-bold tracking-tight text-[#0F172A] md:text-lg">
            The stallholders
          </h2>
          {(board?.stalls || []).length === 0 ? (
            <p className="mt-2 text-sm text-slate-600" data-testid="farmers-public-stalls-empty">
              The first stalls round here are being checked over now. A few hens, a bread oven,
              a glut of courgettes, a hive or a boat is all it takes to open one.
            </p>
          ) : (
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {board.stalls.map((s, i) => (
                <div key={i} className="border border-border bg-white p-4" data-testid={`farmers-public-stall-${i}`}>
                  <p className="text-sm font-semibold text-[#0F172A]">
                    <Storefront size={14} className="mr-1 inline align-[-2px] text-[#0B5C2E]" />{s.name}
                  </p>
                  <p className="text-xs text-slate-500">
                    {s.where}{s.organic ? " · organic" : ""}
                    {s.market_day ? ` · ${s.market_day}` : ""}
                  </p>
                  <p className="mt-1 text-sm text-slate-700">
                    {s.collection ? "Collection at the gate" : "No collection"}
                    {s.delivery
                      ? ` · delivers within ${Number(s.delivery_radius_miles || 0)} miles for ${s.symbol}${Number(s.delivery_fee || 0).toFixed(2)}`
                      : " · no delivery"}
                  </p>
                </div>
              ))}
            </div>
          )}
        </section>

        <NotifyMe source="farmers-market" testid="farmers-notify" />

        <section className="mt-10 border border-border bg-white p-5" data-testid="farmers-public-join">
          <p className="overline text-[#0B5C2E]">Growers, bakers, beekeepers and buyers</p>
          <p className="mt-2 text-sm leading-relaxed text-slate-700">
            Anybody can open a stall — a few hens, a bread oven, a glut of courgettes or a hive at
            the bottom of the garden. Put your own price on it, photograph it the morning it is
            picked, and say how far you will deliver. Buyers pay by card or on handover.
          </p>
          <Link to="/register" className="mt-3 inline-block text-sm font-semibold text-[#0B5C2E] underline"
            data-testid="farmers-public-register">
            Open a stall or start buying
          </Link>
        </section>
      </div>
    </PublicShell>
  );
}
